/**
 * Cheeky Frog — welcome offer
 *
 * Opens once per visitor after a delay, and never again once dismissed or
 * claimed. The markup ships `hidden`, so if this never runs nobody is trapped
 * behind a modal they cannot close — the failure mode is "no offer", not
 * "broken site".
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cf-welcome]');
  if (!root) return;

  var KEY = 'cf-welcome-offer';
  var panel = root.querySelector('.cf-welcome__panel');
  var reduce = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Every storage call is wrapped: private windows and "block site data" both
  // throw on access rather than returning null, and an exception here would
  // take the whole offer down.
  function seen() {
    try {
      var v = localStorage.getItem(KEY);
      return v && Number(v) > Date.now();
    } catch (e) { return false; }
  }

  function remember() {
    var days = Number(root.dataset.remember || 30);
    try {
      localStorage.setItem(KEY, String(Date.now() + days * 86400000));
    } catch (e) { /* it will ask again next visit; that is the lesser evil */ }
  }

  var lastFocus = null;
  var timer = null;

  /* The studio's email gate is a stop: someone has used their free names and
     cannot carry on until they answer it. This is a discount offer. When both
     want the screen the offer waits -- two dialogs at once reads as a broken
     page, and the one that is in the customer's way should be the one they
     can act on. The gate sits above this anyway (z-index 120 against 100), so
     the offer was never hiding it; it was just noise stacked behind it. */
  function gated() {
    return !!document.querySelector('.cf-studio.is-gated');
  }

  function open() {
    if (gated()) { timer = setTimeout(open, 15000); return; }
    lastFocus = document.activeElement;
    root.hidden = false;
    // Next frame, so the transition has a start state to animate from.
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    var target = panel.querySelector('input[type="email"], [data-cf-welcome-code], a');
    if (target) target.focus({ preventScroll: true });
    document.addEventListener('keydown', onKey);
  }

  /* `persist` false means we are standing aside for the gate, not being
     dismissed. Remembering that would cost the visitor the offer entirely for
     a month, having never actually seen it. The click handlers pass an Event,
     which is truthy, so a real dismissal still sticks. */
  function close(persist) {
    root.classList.remove('is-open');
    if (persist !== false) remember();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    var done = function () { root.hidden = true; };
    if (reduce) done();
    else setTimeout(done, 240);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  function onKey(e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    // Keep tabbing inside the dialog while it is modal.
    var f = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  root.querySelectorAll('[data-cf-welcome-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });

  // Copy the code, same behaviour as the announcement bar chips.
  var codeBtn = root.querySelector('[data-cf-welcome-code]');
  if (codeBtn) {
    codeBtn.addEventListener('click', function () {
      var code = codeBtn.dataset.code;
      var label = codeBtn.querySelector('i');
      var write = (navigator.clipboard && window.isSecureContext)
        ? navigator.clipboard.writeText(code)
        : Promise.reject();
      write.then(function () {
        if (!label) return;
        var was = label.textContent;
        label.textContent = 'Copied ✓';
        setTimeout(function () { label.textContent = was; }, 2000);
      }).catch(function () {
        // No clipboard API — select it so a long-press copy still works.
        var r = document.createRange();
        r.selectNodeContents(codeBtn.querySelector('span'));
        var sel = window.getSelection();
        sel.removeAllRanges(); sel.addRange(r);
      });
    });
  }

  // Just signed up: Shopify has bounced the page back with the success state
  // rendered. Show it straight away and do not ask again.
  if (root.querySelector('[data-cf-welcome-code]')) {
    remember();
    open();
    return;
  }

  // A validation error came back — reopen so the message is not stranded on a
  // panel nobody can see.
  if (root.querySelector('.cf-welcome__error')) { open(); return; }

  if (seen()) return;

  var delay = Number(root.dataset.delay || 8) * 1000;
  timer = setTimeout(open, delay);

  /* Already on screen when the gate goes up: step aside rather than sit
     behind it. Watched here rather than announced by the studio, so the
     coupling stays one-way -- the offer knows about the gate, the gate does
     not have to know this exists. */
  var studio = document.querySelector('.cf-studio');
  if (studio && window.MutationObserver) {
    new MutationObserver(function () {
      if (gated() && !root.hidden) close(false);
    }).observe(studio, { attributes: true, attributeFilter: ['class'] });
  }

  // Leaving for the tab bar is the other moment worth catching, and it costs
  // nothing to watch for.
  document.addEventListener('mouseout', function onOut(e) {
    if (e.clientY > 0 || e.relatedTarget || seen()) return;
    document.removeEventListener('mouseout', onOut);
    clearTimeout(timer);
    open();
  });
})();
